import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { BiChevronRight } from 'react-icons/bi';
import { parivartanAPI } from '../../services/parivartanAPI';
import WorkspaceDataTable from '../../components/workspace/WorkspaceDataTable';
import WorkspaceModal from '../../components/workspace/WorkspaceModal';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { clearWorkspaceAccess } from '../../hooks/useWorkspaceAccess';
import { usersListPath } from './settings/settingsPaths';
import { useAdminRecordDelete } from '../../components/workspace/useAdminRecordDelete';
import './LoginHistoryPage.scss';

const UsersAdminPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = usersListPath(location.pathname);
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserId = String(currentUser?.id || currentUser?._id || '');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [effective, setEffective] = useState(null);
  const [error, setError] = useState('');

  useDocumentTitle('Users');

  const load = async () => {
    setLoading(true);
    try {
      const usersRes = await parivartanAPI.listUsers({ limit: 500 });
      setUsers(usersRes.data?.data?.users || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Unable to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const admin = useAdminRecordDelete({
    entity: 'user',
    noun: 'user',
    plural: 'users',
    reload: load,
    label: (row) => row.name,
    allowRow: (row) => String(row._id) !== currentUserId,
    note: 'Your own account stays. A person who already appears on a tender, document, timesheet, or other record stays. Mark that account inactive instead.'
  });

  const tableRows = useMemo(
    () => users.map((u) => ({
      ...u,
      rolesLabel: (u.roles || []).join(', '),
      statusLabel: u.isActive ? 'Active' : 'Inactive'
    })),
    [users]
  );

  const rowActions = (row) => {
    const ownRow = String(row._id) === currentUserId;
    const actions = [
      { type: 'edit', label: 'Edit' },
      { type: 'effective', label: 'Effective permissions', onClick: () => showEffective(row) }
    ];
    if (!ownRow) {
      actions.push({
        type: 'status',
        label: row.isActive ? 'Mark inactive' : 'Mark active',
        onClick: () => setActive(row, !row.isActive)
      });
    }
    if (admin.enabled && !ownRow) {
      actions.push({ type: 'delete', label: 'Delete', variant: 'danger' });
    }
    return actions;
  };

  const columns = useMemo(
    () => [
      { key: 'name', label: 'Name', mobileTitle: true },
      { key: 'email', label: 'Email' },
      {
        key: 'rolesLabel',
        label: 'Roles',
        render: (_, row) => (
          <div className="d-flex flex-wrap gap-1">
            {(row.roles || []).length
              ? (row.roles || []).map((r) => (
                <span key={r} className="badge rounded-pill text-bg-light border text-dark fw-normal">
                  {r}
                </span>
              ))
              : <span className="text-muted small">—</span>}
          </div>
        )
      },
      {
        key: 'statusLabel',
        label: 'Status',
        render: (value) => (
          <span className={`badge ${value === 'Active' ? 'bg-success' : 'bg-danger'}`}>{value}</span>
        )
      }
    ],
    []
  );

  const openEdit = (row) => {
    navigate(`${listPath}/${row._id}/edit`);
  };

  const setActive = async (row, isActive) => {
    setError('');
    try {
      await parivartanAPI.updateUser(row._id, { isActive });
      clearWorkspaceAccess();
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Could not change the account status');
    }
  };

  const showEffective = async (row) => {
    const r = await parivartanAPI.getUserPermissions(row._id);
    setEffective({ user: row, ...r.data?.data });
  };

  return (
    <div>
      <WorkspacePageHeader
        title="Users"
        subtitle="Add people, assign roles, and review who can sign in. Delete is limited to platform administrators and is refused when the person already appears on a record."
        actions={(
          <Button variant="primary" onClick={() => navigate(`${listPath}/new`)}>Add user</Button>
        )}
      />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}

      <div className="admin-entry-row">
        <button type="button" className="login-entry" onClick={() => navigate(`${listPath}/login-history`)}>
          <span className="login-entry__mark" aria-hidden>LH</span>
          <span>
            <strong>Login history</strong>
            <small>Who is signed in, when they last signed in, and from which address.</small>
          </span>
          <BiChevronRight size={20} aria-hidden />
        </button>
        <button type="button" className="login-entry" onClick={() => navigate('/platform/audit')}>
          <span className="login-entry__mark" aria-hidden>AT</span>
          <span>
            <strong>Audit trail</strong>
            <small>Saves, edits, deletes, and sign-ins, with the screen and address.</small>
          </span>
          <BiChevronRight size={20} aria-hidden />
        </button>
      </div>

      {loading ? (
        <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>
      ) : (
        <WorkspaceDataTable
          title="User directory"
          downloadPermission="users.download"
          data={tableRows}
          columns={columns}
          searchPlaceholder="Search name or email…"
          emptyMessage="No users match your search"
          pageSize={10}
          onEdit={openEdit}
          {...admin.tableProps}
          rowActions={rowActions}
        />
      )}
      {admin.modal}

      <WorkspaceModal
        show={!!effective}
        onHide={() => setEffective(null)}
        title="Effective permissions"
        description="Denials override grants and role permissions."
        userProfile={effective?.user ? { name: effective.user.name, email: effective.user.email } : null}
        footer={<Button variant="outline-secondary" onClick={() => setEffective(null)}>Close</Button>}
      >
        <section className="workspace-modal__section">
          <div className="workspace-modal__section-label">Effective set ({(effective?.effective || []).length})</div>
          <div className="workspace-modal__perm-list">
            {(effective?.effective || []).length
              ? (effective.effective).map((p) => (
                <span key={p} className="workspace-modal__perm-chip">{p}</span>
              ))
              : <span className="text-muted small">No permissions</span>}
          </div>
        </section>
        <section className="workspace-modal__section">
          <div className="workspace-modal__section-label">Contributions by role</div>
          {(effective?.sources || []).map((s) => (
            <div key={s.role} className="workspace-modal__role-card">
              <strong>{s.role}</strong>
              <div className="small mt-1">{s.permissions.join(' · ')}</div>
            </div>
          ))}
        </section>
      </WorkspaceModal>
    </div>
  );
};

export default UsersAdminPage;
